import React, { useState, useEffect, useMemo, forwardRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useUser } from '../../contexts/UserContext';
import FormField from '../ui/FormField';
import FormFieldWithVerification from '../ui/FormFieldWithVerification';
import ProgressIndicator from '../ui/ProgressIndicator';
import TextAreaField from '../ui/TextAreaField';
import FormHeader from './FormHeader';
import { Mail, Phone, User, Briefcase, FileText } from '../common/IconsComponent';
import SpinnerOverlay from '../ui/SpinnerOverlay';

// ─── Schema ───────────────────────────────────────────────────────────────────
// Three different shapes depending on role: a supplier saves its
// learning-center name + contact person, a school saves its own name + principal + institution
// code (none of which exist for anyone else), and everyone else keeps
// the original firstName/lastName pair. Built as a function since which
// shape applies is only known once we have the user's role.
const buildPersonalInfoSchema = (isSupplier, isSchool) =>
  Yup.object().shape({
    ...(isSupplier
      ? {
          businessName: Yup.string().min(2).max(100).required('שדה חובה'),
          contactPerson: Yup.string().min(2).max(50).required('שדה חובה'),
        }
      : isSchool
      ? {
          schoolName: Yup.string().min(2).max(100).required('שדה חובה'),
          principalName: Yup.string().min(2).max(50).required('שדה חובה'),
          institutionCode: Yup.string()
            .required('שדה חובה')
            .matches(/^\d{5,7}$/, 'סמל מוסד לא תקין'),
        }
      : {
          firstName: Yup.string().min(2).max(20).required('שדה חובה'),
          lastName:  Yup.string().min(2).max(20).required('שדה חובה'),
        }),
    email: Yup.string().email().required('שדה חובה'),
    phone: Yup.string().required().matches(/^05\d-?\d{7}$/, 'מספר טלפון לא תקין'),
    aboutMe: Yup.string().nullable().test(
      'len', '20-200 תווים',
      (val) => !val || (val.length >= 20 && val.length <= 200)
    ),
  });

// ─── Helpers ─────────────────────────────────────────────────────────────────

const FIELD_ICONS = {
  email:           <Mail      size={15} className="text-indigo-500" />,
  phone:           <Phone     size={15} className="text-indigo-500" />,
  firstName:       <User      size={15} className="text-indigo-500" />,
  lastName:        <User      size={15} className="text-indigo-500" />,
  businessName:    <Briefcase size={15} className="text-indigo-500" />,
  contactPerson:   <User      size={15} className="text-indigo-500" />,
  department:      <Briefcase size={15} className="text-indigo-500" />,
  schoolName:      <Briefcase size={15} className="text-indigo-500" />,
  principalName:   <User      size={15} className="text-indigo-500" />,
  institutionCode: <FileText  size={15} className="text-indigo-500" />,
};

const INIT_VERIFICATION = { field: null, code: '', showInput: false, nonce: null };

const isFieldConfirmed = (user, field) => {
  const camel = field === 'email' ? user.emailConfirmed : user.phoneConfirmed;
  const snake = field === 'email' ? user.email_confirmed : user.phone_confirmed;
  return [1, true, '1'].includes(camel) || [1, '1'].includes(snake);
};

// ─── Component ───────────────────────────────────────────────────────────────

const PersonalInfoSection = forwardRef(({ active, onSubmit }, ref) => {
  const {
    user, updateUserProfile,
    showAlert, sendVerificationCode, checkVerificationCode,
  } = useUser();

  const safeUser = user || {};

  // A supplier saves ONLY the business name here — no first/last name
  // at all, and it's written to supplier_profiles.supplier_name
  // server-side, not the `users` table (see UsersController::update()
  // and the handleFormSubmit payload split below). A school saves its
  // own name + principal name + institution code instead, also with no
  // first/last name. Every other role keeps the original
  // firstName/lastName pair on `users`.
  const role = safeUser.role || 'student';
  const isSupplier = role === 'supplier';
  const isSchool = role === 'school';

  const personalInfoSchema = useMemo(
    () => buildPersonalInfoSchema(isSupplier, isSchool),
    [isSupplier, isSchool]
  );

  const copy = isSupplier
    ? { sectionTitle: 'פרטי מרכז הלמידה', aboutLabel: 'על מרכז הלמידה' }
    : isSchool
    ? { sectionTitle: 'פרטי בית הספר', aboutLabel: 'על בית הספר' }
    : { sectionTitle: 'מידע אישי', aboutLabel: 'על עצמי' };

  const [originalData,         setOriginalData        ] = useState({ email: safeUser.email || '', phone: safeUser.phone || '' });
  const [fieldsModified,       setFieldsModified       ] = useState({ email: false, phone: false });
  const [verification,         setVerification         ] = useState(INIT_VERIFICATION);
  const [verificationAttempts, setVerificationAttempts] = useState({ email: 0, phone: 0 });
  const [isLoading,            setIsLoading            ] = useState(false);
  const [formProgress,         setFormProgress         ] = useState(0);

  const formik = useFormik({
    initialValues: {
      firstName:       safeUser.firstName       || safeUser.first_name       || '',
      lastName:        safeUser.lastName        || safeUser.last_name        || '',
      businessName:    safeUser.businessName    || safeUser.supplier_name    || safeUser.business_name    || '',
      contactPerson:   safeUser.contactPerson   || safeUser.contact_person   || '',
      schoolName:      safeUser.schoolName      || safeUser.school_name      || '',
      principalName:   safeUser.principalName   || safeUser.principal_name   || '',
      institutionCode: safeUser.institutionCode || safeUser.institution_code || '',
      email:           safeUser.email           || '',
      phone:           safeUser.phone           || '',
      department:      safeUser.department      || '',
      aboutMe:         safeUser.aboutMe         || safeUser.about_me         || '',
    },
    enableReinitialize: true,
    validationSchema: personalInfoSchema,
    onSubmit: async (values, helpers) => {
      setIsLoading(true);
      try {
        // Scoped so a supplier/school never sends fields that belong to
        // another role (which would be empty strings here, since those
        // fields don't render for them, and would otherwise silently
        // blank out whatever's on `users`/the role's own table today).
        const payload = isSupplier
          ? { businessName: values.businessName, contactPerson: values.contactPerson, email: values.email, phone: values.phone, aboutMe: values.aboutMe }
          : isSchool
          ? {
              schoolName: values.schoolName,
              principalName: values.principalName,
              institutionCode: values.institutionCode,
              email: values.email,
              phone: values.phone,
              aboutMe: values.aboutMe,
            }
          : { firstName: values.firstName, lastName: values.lastName, email: values.email, phone: values.phone, aboutMe: values.aboutMe };
        const result = await updateUserProfile(payload);

        // updateUserProfile reports failure via { success:false } instead
        // of throwing (and already shows the server's error message), so
        // this used to fall through to the success path. On failure —
        // e.g. the email already belongs to another user — put the last
        // saved email/phone back into the fields and stop.
        if (result && result.success === false) {
          helpers.setFieldValue('email', originalData.email || '', false);
          helpers.setFieldValue('phone', originalData.phone || '', false);
          helpers.setFieldTouched('email', false, false);
          helpers.setFieldTouched('phone', false, false);
          setFieldsModified({ email: false, phone: false });
          return;
        }

        setOriginalData({ email: values.email, phone: values.phone });
        setFieldsModified({ email: false, phone: false });
        showAlert('המידע נשמר בהצלחה', 'success');
        onSubmit?.(values);
      } catch (err) {
        showAlert(err?.message || 'אירעה שגיאה בעת שמירת המידע');
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Progress
  useEffect(() => {
    const required = isSupplier
      ? ['businessName', 'contactPerson', 'email', 'phone']
      : isSchool
      ? ['schoolName', 'principalName', 'institutionCode', 'email', 'phone']
      : ['firstName', 'lastName', 'email', 'phone'];
    const filled   = required.filter((f) => formik.values[f]).length;
    const verified = (['email', 'phone'].filter((f) => isFieldConfirmed(safeUser, f))).length;
    setFormProgress(Math.min(100, Math.round((filled / required.length) * 70 + (verified / 2) * 30)));
  }, [
    isSupplier, isSchool,
    formik.values.firstName, formik.values.lastName, formik.values.businessName, formik.values.contactPerson,
    formik.values.schoolName, formik.values.principalName, formik.values.institutionCode,
    formik.values.email,     formik.values.phone,
    safeUser.emailConfirmed, safeUser.email_confirmed,
    safeUser.phoneConfirmed, safeUser.phone_confirmed,
  ]);

  // Track email/phone modifications
  useEffect(() => {
    ['email', 'phone'].forEach((field) => {
      const modified = formik.values[field] !== originalData[field];
      if (modified !== fieldsModified[field]) {
        // Display-only: while the field differs from the saved value the
        // "verified" badge is hidden (see shouldShowVerified). The server
        // is NOT told the field is unconfirmed here — that used to wipe
        // the verified status of the current, still-saved value as soon as
        // the user typed, even if the change was later cancelled/failed.
        // The real reset happens server-side only when a new value is saved.
        setFieldsModified((prev) => ({ ...prev, [field]: modified }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.email, formik.values.phone, originalData.email, originalData.phone]);

  const handleSendCode = async (field) => {
    try {
      await personalInfoSchema.validateAt(field, formik.values);
      setVerification({ field, code: '', showInput: true, nonce: null });
      setIsLoading(true);
      const resp = await sendVerificationCode(field, formik.values[field], { timeout: 7000 });
      setVerification((prev) => ({ ...prev, nonce: resp?.nonce || null }));
    } catch (err) {
      setVerification(INIT_VERIFICATION);
      formik.setFieldError(field, err?.message || 'שגיאה בשליחת קוד');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    const { field, code, nonce } = verification;
    if (!field || !code) return;
    setIsLoading(true);
    try {
      await checkVerificationCode(field, code, nonce, { timeout: 7000 });
      setVerification(INIT_VERIFICATION);
      setVerificationAttempts((prev) => ({ ...prev, [field]: 0 }));
      setOriginalData((prev) => ({ ...prev, [field]: formik.values[field] }));
      setFieldsModified((prev) => ({ ...prev, [field]: false }));
    } catch (err) {
      const isWrongCode = /קוד.*שגוי|invalid code/i.test(err?.message || '');
      if (isWrongCode) {
        const attempts = (verificationAttempts[field] || 0) + 1;
        setVerificationAttempts((prev) => ({ ...prev, [field]: attempts }));
        if (attempts >= 3) setVerification(INIT_VERIFICATION);
        else               setVerification((prev) => ({ ...prev, code: '' }));
        formik.setFieldError(field, 'קוד האימות שגוי');
      } else {
        setVerification((prev) => ({ ...prev, code: '' }));
        formik.setFieldError(field, err?.message || 'אירעה שגיאה. נסה שוב מאוחר יותר');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const shouldShowVerified = (field) =>
    !fieldsModified[field] && isFieldConfirmed(safeUser, field);

  const shouldShowVerifyButton = (field) =>
    !fieldsModified[field] &&
    !isFieldConfirmed(safeUser, field) &&
    !(verification.field === field && verification.showInput);

  const onCodeChange = (e) =>
    setVerification((prev) => ({ ...prev, code: e.target.value.replace(/[^0-9]/g, '') }));

  if (!active) return null;

  return (
    <div dir="rtl" ref={ref} id="personal-info-section" className="scroll-mt-24">
      <div className="bg-white rounded-2xl shadow-md border border-indigo-50 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6">

        {isLoading && <SpinnerOverlay variant="glow" text="נא להמתין" dark={false} blur={false} />}

        <form onSubmit={formik.handleSubmit} noValidate>
          <FormHeader
            title={copy.sectionTitle}
            buttonText="שמירת שינויים"
            disabled={isLoading || verification.showInput}
          />

          <ProgressIndicator progress={formProgress} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 sm:gap-y-5 mt-4">

            {/* Name row — supplier saves only a business name; a school
                saves its own name (full width) plus principal name and
                institution code (two columns, like firstName/lastName). */}
            {isSupplier ? (
              <>
                <FormField
                  label="שם מרכז הלימוד"
                  name="businessName"
                  value={formik.values.businessName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  icon={FIELD_ICONS.businessName}
                  required
                  error={formik.touched.businessName && formik.errors.businessName}
                />
                <FormField
                  label="שם איש קשר"
                  name="contactPerson"
                  value={formik.values.contactPerson}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  icon={FIELD_ICONS.contactPerson}
                  required
                  error={formik.touched.contactPerson && formik.errors.contactPerson}
                />
              </>
            ) : isSchool ? (
              <>
                <div className="sm:col-span-2">
                  <FormField
                    label="שם בית הספר"
                    name="schoolName"
                    value={formik.values.schoolName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    icon={FIELD_ICONS.schoolName}
                    required
                    error={formik.touched.schoolName && formik.errors.schoolName}
                  />
                </div>
                <FormField
                  label="שם מנהל/ת"
                  name="principalName"
                  value={formik.values.principalName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  icon={FIELD_ICONS.principalName}
                  required
                  error={formik.touched.principalName && formik.errors.principalName}
                />
                <FormField
                  label="סמל מוסד"
                  name="institutionCode"
                  value={formik.values.institutionCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  icon={FIELD_ICONS.institutionCode}
                  required
                  inputMode="numeric"
                  error={formik.touched.institutionCode && formik.errors.institutionCode}
                />
              </>
            ) : (
              <>
                <FormField
                  label="שם פרטי"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  icon={FIELD_ICONS.firstName}
                  required
                  error={formik.touched.firstName && formik.errors.firstName}
                />
                <FormField
                  label="שם משפחה"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  icon={FIELD_ICONS.lastName}
                  required
                  error={formik.touched.lastName && formik.errors.lastName}
                />
              </>
            )}

            {/* Email — full width */}
            <div className="sm:col-span-2">
              <FormFieldWithVerification
                label="דואר אלקטרוני"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                icon={FIELD_ICONS.email}
                required
                error={formik.touched.email && formik.errors.email}
                isVerified={shouldShowVerified('email')}
                showVerificationInput={verification.field === 'email' && verification.showInput}
                verificationCode={verification.code}
                onSendCode={() => handleSendCode('email')}
                onVerificationCodeChange={onCodeChange}
                onVerify={handleVerify}
                verificationAttempts={verificationAttempts.email}
                maxAttempts={3}
                showVerificationButton={shouldShowVerifyButton('email')}
              />
            </div>

            {/* Phone — full width */}
            <div className="sm:col-span-2">
              <FormFieldWithVerification
                label="טלפון נייד"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                icon={FIELD_ICONS.phone}
                required
                error={formik.touched.phone && formik.errors.phone}
                isVerified={shouldShowVerified('phone')}
                showVerificationInput={verification.field === 'phone' && verification.showInput}
                verificationCode={verification.code}
                onSendCode={() => handleSendCode('phone')}
                onVerificationCodeChange={onCodeChange}
                onVerify={handleVerify}
                verificationAttempts={verificationAttempts.phone}
                maxAttempts={3}
                showVerificationButton={shouldShowVerifyButton('phone')}
              />
            </div>

            {/* About — full width */}
            <div className="sm:col-span-2">
              <TextAreaField
                label={copy.aboutLabel}
                name="aboutMe"
                value={formik.values.aboutMe}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                icon={<FileText size={15} />}
                maxLength={200}
                minLength={20}
                error={formik.touched.aboutMe && formik.errors.aboutMe}
                dir="rtl"
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
});

export default PersonalInfoSection;